(() => {
  'use strict';

  const EDITIONS = [
    { key: 'cream-classic', value: 'Cream Classic', bg: '#F3EFE6', ink: '#0D1B2A', accent: '#A9895F' },
    { key: 'heritage-navy', value: 'Heritage Navy', bg: '#0D1B2A', ink: '#F3EFE6', accent: '#C7A352' },
    { key: 'heirloom-rose', value: 'Heirloom Rose', bg: '#F1E4DE', ink: '#0D1B2A', accent: '#C98F8A' }
  ];

  const normalize = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  function money(cents) {
    const currency = window.EvrKinTheme?.currency || window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  function setupMobileMenu(scope = document) {
    scope.querySelectorAll('[data-site-header]').forEach((header) => {
      if (header.dataset.menuInitialized === 'true') return;
      header.dataset.menuInitialized = 'true';

      const button = header.querySelector('[data-mobile-menu-button]');
      const panel = header.querySelector('[data-mobile-panel]');
      if (!button || !panel) return;

      const closeMenu = () => {
        button.setAttribute('aria-expanded', 'false');
        button.textContent = 'Menu';
        panel.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('menu-open');
      };

      button.addEventListener('click', () => {
        const willOpen = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(willOpen));
        button.textContent = willOpen ? 'Close' : 'Menu';
        panel.setAttribute('aria-hidden', String(!willOpen));
        document.body.classList.toggle('menu-open', willOpen);
      });

      panel.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeMenu();
      });
    });
  }

  async function refreshCartCount() {
    const url = window.EvrKinTheme?.cartJsonUrl || '/cart.js';
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = cart.item_count;
      });
    } catch (_) {
      // The cart still works without the count refresh.
    }
  }

  function setupProduct(root) {
    if (!root || root.dataset.productInitialized === 'true') return;
    root.dataset.productInitialized = 'true';

    const productData = {};
    root.querySelectorAll('[data-product-json]').forEach((script) => {
      try {
        const tier = script.dataset.productJson;
        productData[tier] = JSON.parse(script.textContent);
      } catch (_) {
        // Keep the rest of the page functional if preview data is incomplete.
      }
    });

    if (!productData.bespoke && !productData.standard) return;
    if (!productData.bespoke) productData.bespoke = productData.standard;
    if (!productData.standard) productData.standard = productData.bespoke;

    const state = {
      tier: root.dataset.defaultTier === 'standard' ? 'standard' : 'bespoke',
      edition: 'Cream Classic',
      configurationVariantId: null,
      variant: null
    };

    const price = root.querySelector('[data-price]');
    const priceNote = root.querySelector('[data-price-note]');
    const primaryCta = root.querySelector('[data-primary-cta]');
    const stickyCta = root.querySelector('[data-sticky-cta]');
    const stickyPrice = root.querySelector('[data-sticky-price]');
    const stickyBuy = root.querySelector('[data-sticky-buy]');
    const status = root.querySelector('[data-product-status]');
    const extraOptions = root.querySelector('[data-extra-options]');
    const productImage = root.querySelector('[data-product-image]');
    const bookFallback = root.querySelector('[data-book-fallback]');
    const buyBox = root.querySelector('[data-main-buy-box]');

    const params = new URLSearchParams(window.location.search);
    const requestedEdition = normalize(params.get('edition'));
    const requested = EDITIONS.find((edition) => edition.key === requestedEdition);
    if (requested) state.edition = requested.value;

    const currentProduct = () => productData[state.tier] || productData.bespoke;

    function optionNames(product) {
      return Array.isArray(product.options) ? product.options : [];
    }

    function editionOptionIndex(product) {
      const options = optionNames(product);
      const index = options.findIndex((name) => normalize(name).includes('edition'));
      return index >= 0 ? index : 0;
    }

    function availableEditions(product) {
      const index = editionOptionIndex(product);
      return new Set((product.variants || []).map((variant) => variant.options?.[index]).filter(Boolean));
    }

    function syncEditionControls() {
      const product = currentProduct();
      const available = availableEditions(product);
      if (available.size && !available.has(state.edition)) {
        const firstKnown = EDITIONS.find((edition) => available.has(edition.value));
        state.edition = firstKnown?.value || [...available][0];
      }

      root.querySelectorAll('[data-edition-value]').forEach((control) => {
        const isAvailable = available.size === 0 || available.has(control.dataset.editionValue);
        const active = control.dataset.editionValue === state.edition;
        control.disabled = !isAvailable;
        control.classList.toggle('active', active);
        control.setAttribute('aria-pressed', String(active));
      });
    }

    function buildExtraOptions() {
      if (!extraOptions) return;
      extraOptions.innerHTML = '';
      const product = currentProduct();
      const options = optionNames(product);
      const eIndex = editionOptionIndex(product);
      const finishIndexes = options.map((_, index) => index).filter((index) => index !== eIndex);
      const candidates = (product.variants || []).filter((variant) => variant.options?.[eIndex] === state.edition);

      if (!finishIndexes.length || candidates.length <= 1) {
        state.configurationVariantId = candidates.find((variant) => variant.available)?.id || candidates[0]?.id || null;
        return;
      }

      const wrapper = document.createElement('div');
      wrapper.className = 'dynamic-option';
      const label = document.createElement('label');
      const id = `EvrKinConfiguration-${root.dataset.sectionId}`;
      label.htmlFor = id;
      label.textContent = state.tier === 'bespoke' ? 'Bespoke Finish' : 'Configuration';

      const select = document.createElement('select');
      select.id = id;
      select.dataset.configuration = 'true';

      const validPrevious = candidates.some((variant) => String(variant.id) === String(state.configurationVariantId));
      const initial = validPrevious
        ? candidates.find((variant) => String(variant.id) === String(state.configurationVariantId))
        : (candidates.find((variant) => variant.available) || candidates[0]);
      state.configurationVariantId = initial?.id || null;

      candidates.forEach((variant) => {
        const option = document.createElement('option');
        option.value = String(variant.id);
        const finishLabel = finishIndexes.map((index) => variant.options?.[index]).filter(Boolean).join(' · ');
        option.textContent = finishLabel || variant.title || 'Configuration';
        option.disabled = !variant.available;
        option.selected = String(variant.id) === String(state.configurationVariantId);
        select.appendChild(option);
      });

      select.addEventListener('change', () => {
        state.configurationVariantId = Number(select.value);
        updateVariant();
      });

      wrapper.append(label, select);
      extraOptions.appendChild(wrapper);
    }

    function findVariant() {
      const product = currentProduct();
      const eIndex = editionOptionIndex(product);
      const candidates = (product.variants || []).filter((variant) => variant.options?.[eIndex] === state.edition);

      if (state.configurationVariantId != null) {
        const configured = candidates.find((variant) => String(variant.id) === String(state.configurationVariantId));
        if (configured) return configured;
      }

      return candidates.find((variant) => variant.available)
        || candidates[0]
        || (product.variants || []).find((variant) => variant.available)
        || product.variants?.[0]
        || null;
    }

    function updateBookFallback() {
      if (!bookFallback) return;
      const edition = EDITIONS.find((item) => item.value === state.edition) || EDITIONS[0];
      bookFallback.style.background = edition.bg;
      bookFallback.style.color = edition.ink;
      const accentNode = bookFallback.querySelector('.the');
      if (accentNode) accentNode.style.color = edition.accent;
    }

    function updateMedia(variant) {
      const product = currentProduct();
      const featured = variant?.featured_image || product.featured_image;
      let src = '';
      if (featured) src = typeof featured === 'string' ? featured : (featured.src || '');

      if (src && productImage) {
        productImage.src = src;
        productImage.alt = featured?.alt || `${product.title || 'The Manual'} — ${state.edition}`;
        productImage.classList.remove('is-hidden');
        bookFallback?.classList.add('is-hidden');
      } else {
        productImage?.classList.add('is-hidden');
        bookFallback?.classList.remove('is-hidden');
        updateBookFallback();
      }
    }

    function updateTierControls() {
      root.querySelectorAll('[data-tier]').forEach((control) => {
        const active = control.dataset.tier === state.tier;
        control.classList.toggle('active', active);
        control.setAttribute('aria-pressed', String(active));
      });
    }

    function updateTierLinks() {
      const edition = EDITIONS.find((item) => item.value === state.edition);
      if (!edition) return;
      document.querySelectorAll('[data-tier-link]').forEach((link) => {
        const tier = link.dataset.tierLink;
        const targetProduct = productData[tier];
        if (!targetProduct?.url) return;
        const target = new URL(targetProduct.url, window.location.origin);
        target.searchParams.set('edition', edition.key);
        link.href = `${target.pathname}${target.search}${target.hash}`;
      });
    }

    function updateUrl() {
      const edition = EDITIONS.find((item) => item.value === state.edition);
      if (!edition) return;
      updateTierLinks();
      if (!window.history?.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('edition', edition.key);
      window.history.replaceState({}, '', url);
    }

    function updateVariant() {
      syncEditionControls();
      const variant = findVariant();
      state.variant = variant;

      const label = state.tier === 'bespoke' ? 'Bespoke' : 'Standard';
      if (priceNote) priceNote.textContent = label;

      if (!variant) {
        if (price) price.textContent = 'Unavailable';
        if (stickyPrice) stickyPrice.textContent = 'Unavailable';
        if (primaryCta) {
          primaryCta.textContent = 'Unavailable';
          primaryCta.disabled = true;
        }
        if (stickyCta) {
          stickyCta.textContent = 'Unavailable';
          stickyCta.disabled = true;
        }
        return;
      }

      const formatted = money(variant.price);
      if (price) price.textContent = formatted;
      if (stickyPrice) stickyPrice.textContent = formatted;

      const available = Boolean(variant.available);
      const ctaText = available ? `Add ${label} to Bag` : 'Sold Out';
      [primaryCta, stickyCta].forEach((button) => {
        if (!button) return;
        button.textContent = ctaText;
        button.disabled = !available;
      });

      updateMedia(variant);
      updateUrl();
    }

    function setEdition(value) {
      state.edition = value;
      syncEditionControls();
      buildExtraOptions();
      updateVariant();
    }

    function setTier(tier) {
      if (!productData[tier]) return;
      state.tier = tier;
      state.configurationVariantId = null;
      updateTierControls();
      syncEditionControls();
      buildExtraOptions();
      updateVariant();
    }

    async function addToBag(trigger) {
      const variant = state.variant;
      if (!variant?.id || !variant.available) return;

      const buttons = [primaryCta, stickyCta].filter(Boolean);
      buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));
      if (status) status.textContent = 'Adding to Bag…';

      try {
        const response = await fetch(window.EvrKinTheme?.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Unable to add this item.');

        if (status) status.textContent = 'Added to Bag.';
        buttons.forEach((button) => {
          button.dataset.originalText = button.textContent;
          button.textContent = 'Added to Bag';
        });
        await refreshCartCount();
        window.setTimeout(() => {
          updateVariant();
          if (status) status.textContent = '';
        }, 1400);
      } catch (error) {
        if (status) status.textContent = error.message || 'Unable to add this item. Please try again.';
      } finally {
        buttons.forEach((button) => button.removeAttribute('aria-busy'));
        trigger?.focus?.({ preventScroll: true });
      }
    }

    root.querySelectorAll('[data-edition-value]').forEach((control) => {
      control.addEventListener('click', () => setEdition(control.dataset.editionValue));
    });
    root.querySelectorAll('[data-tier]').forEach((control) => {
      control.addEventListener('click', () => setTier(control.dataset.tier));
    });
    primaryCta?.addEventListener('click', () => addToBag(primaryCta));
    stickyCta?.addEventListener('click', () => addToBag(stickyCta));

    syncEditionControls();
    updateTierControls();
    buildExtraOptions();
    updateVariant();

    if (stickyBuy && buyBox && 'IntersectionObserver' in window) {
      const setHeaderOffset = () => {
        const header = document.querySelector('[data-site-header]');
        const height = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
        document.documentElement.style.setProperty('--sticky-header-offset', `${height}px`);
      };
      setHeaderOffset();
      window.addEventListener('resize', setHeaderOffset, { passive: true });

      const observer = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        stickyBuy.classList.toggle('is-visible', passed);
        stickyBuy.setAttribute('aria-hidden', String(!passed));
      }, { threshold: 0.02 });
      observer.observe(buyBox);
    }
  }

  function setupAll(scope = document) {
    setupMobileMenu(scope);
    scope.querySelectorAll('[data-evrkin-product-root]').forEach(setupProduct);
  }

  document.addEventListener('DOMContentLoaded', () => setupAll(document));
  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
})();
